import { Component, Input } from '@angular/core';
import {
    getWhatsappDeliveryStatusLabel,
    getWhatsappDeliveryStatusSeverity,
    getWhatsappEventTypeLabel,
    getWhatsappEventTypeSeverity,
    getWhatsappMessageTypeLabel,
    WHATSAPP_CONVERSATION_DIRECTION,
    WHATSAPP_MESSAGE_TYPE,
    WhatsappConversationSummary,
    WhatsappConversationEvent,
    WhatsappConversationMessage,
} from 'src/app/models/notificacion/whatsapp_conversation.model';
import {
    IPasoRegistroLectura,
    NODO_TIPO_ICONO,
    NODO_TIPO_LABEL,
    NodoTipo,
} from 'src/app/models/notificacion/whatsapp_flujo.model';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-whatsapp-conversation-timeline',
    templateUrl: './whatsapp-conversation-timeline.component.html',
    styleUrls: ['./whatsapp-conversation-timeline.component.scss']
})
export class WhatsappConversationTimelineComponent {

    @Input() conversation: WhatsappConversationSummary | null = null;
    @Input() messages: WhatsappConversationMessage[] = [];
    @Input() events: WhatsappConversationEvent[] = [];
    /** Registro (outcome) del flujo de esta conversación (S5), ya ordenado por `orden` ASC por el backend. */
    @Input() pasosFlujo: IPasoRegistroLectura[] = [];
    @Input() loadingPasosFlujo = false;

    readonly outboundCode = WHATSAPP_CONVERSATION_DIRECTION.OUTBOUND;
    readonly interactiveMessageCode = WHATSAPP_MESSAGE_TYPE.INTERACTIVE;
    readonly templateMessageCode = WHATSAPP_MESSAGE_TYPE.TEMPLATE;

    getMessageRowClasses(message: WhatsappConversationMessage): Record<string, boolean> {
        return {
            outbound: message.direction_code === this.outboundCode,
            inbound: message.direction_code !== this.outboundCode,
            'system-generated': this.isSystemMessage(message),
        };
    }

    getMessageBubbleClasses(message: WhatsappConversationMessage): Record<string, boolean> {
        return {
            'compact-image-only': this.isImageAttachment(message) && !this.hasVisibleBody(message),
            'compact-file-only': this.hasAttachment(message) && !this.isImageAttachment(message) && !this.hasVisibleBody(message),
        };
    }

    getDeliveryLabel(deliveryStatusCode: number | null): string {
        return getWhatsappDeliveryStatusLabel(deliveryStatusCode);
    }

    getDeliverySeverity(deliveryStatusCode: number | null): string {
        return getWhatsappDeliveryStatusSeverity(deliveryStatusCode);
    }

    /** Solo se marca lo que no es texto libre — un template o un tap interactivo aportan contexto que el body solo no transmite. */
    showMessageTypeTag(message: WhatsappConversationMessage): boolean {
        return message.message_type_code === this.interactiveMessageCode || message.message_type_code === this.templateMessageCode;
    }

    getMessageTypeLabel(message: WhatsappConversationMessage): string {
        return getWhatsappMessageTypeLabel(message.message_type_code);
    }

    getEventTypeLabel(event: WhatsappConversationEvent): string {
        return getWhatsappEventTypeLabel(event);
    }

    getEventTypeSeverity(event: WhatsappConversationEvent): string {
        return getWhatsappEventTypeSeverity(event.event_type_code);
    }

    /** El paso 0 es el mensaje de campaña que arrancó el flujo (D6) — parte del recorrido, no un paso "raro" a esconder. */
    esPasoDeCampana(paso: IPasoRegistroLectura): boolean {
        return Number(paso.orden) === 0;
    }

    getNodoTipoLabel(paso: IPasoRegistroLectura): string {
        return NODO_TIPO_LABEL[paso.nodoTipoCode as NodoTipo] || 'Paso';
    }

    getNodoTipoIcono(paso: IPasoRegistroLectura): string {
        return NODO_TIPO_ICONO[paso.nodoTipoCode as NodoTipo] || 'pi pi-circle';
    }

    getActorLabel(message: WhatsappConversationMessage): string {
        if (this.isSystemMessage(message)) {
            return 'Sistema';
        }

        return message.actor_user_name || (message.direction_code === this.outboundCode ? 'Asesor' : 'Cliente');
    }

    hasProviderError(message: WhatsappConversationMessage): boolean {
        return Boolean(String(message.provider_error_detail || '').trim());
    }

    hasVisibleBody(message: WhatsappConversationMessage): boolean {
        return Boolean(String(message.body_text || '').trim());
    }

    /** Se lee de `metadata.raw` para que las filas viejas, guardadas sin body, también se vean como reacción. */
    isReaction(message: WhatsappConversationMessage): boolean {
        return this.getRawPayload(message)?.['type'] === 'reaction';
    }

    /** Texto de una reacción; el emoji sale del crudo de Meta y, si falta, del body guardado. */
    getReactionLabel(message: WhatsappConversationMessage): string {
        const reaction = this.getRawPayload(message)?.['reaction'] as Record<string, unknown> | undefined;
        const emoji = String(reaction?.['emoji'] || message.body_text || '').trim();

        return emoji ? `Reaccionó con ${emoji}` : 'Quitó su reacción';
    }

    hasAttachment(message: WhatsappConversationMessage): boolean {
        return Boolean(this.getAttachmentUrl(message));
    }

    isImageAttachment(message: WhatsappConversationMessage): boolean {
        const attachmentUrl = this.getAttachmentUrl(message);
        if (!attachmentUrl) {
            return false;
        }

        const normalizedContentType = this.getAttachmentContentType(message);
        if (normalizedContentType?.startsWith('image/')) {
            return true;
        }

        if (/\/image\/upload\//i.test(attachmentUrl)) {
            return true;
        }

        return /\.(avif|bmp|gif|ico|jpe?g|png|svg|tiff?|webp)(\?.*)?$/i.test(attachmentUrl);
    }

    getAttachmentUrl(message: WhatsappConversationMessage): string | null {
        const mediaUrl = this.normalizeAttachmentSource(message.media_url);
        if (!mediaUrl) {
            return null;
        }

        if (/^(https?:|data:|blob:)/i.test(mediaUrl)) {
            return mediaUrl;
        }

        if (/^[^\\/]+\.[a-z0-9]{2,8}(\?.*)?$/i.test(mediaUrl)) {
            return `${environment.host}/get_file/notificacion/${encodeURIComponent(mediaUrl)}`;
        }

        return mediaUrl;
    }

    getAttachmentLabel(message: WhatsappConversationMessage): string {
        const attachmentKind = this.getAttachmentKind(message);
        if (attachmentKind) {
            return attachmentKind;
        }

        return 'Archivo adjunto';
    }

    getAttachmentMeta(message: WhatsappConversationMessage): string {
        const normalizedContentType = this.getAttachmentContentType(message);
        if (normalizedContentType) {
            return normalizedContentType.replace('/', ' · ');
        }

        return 'Tocar para abrir';
    }

    private getAttachmentContentType(message: WhatsappConversationMessage): string | null {
        const directContentType = String(message.media_content_type || '').trim().toLowerCase();
        if (directContentType) {
            return directContentType;
        }

        const metadataContentType = String(
            message.metadata?.['media_content_type']
            || message.metadata?.['contentType']
            || (message.metadata?.['raw'] as Record<string, unknown> | undefined)?.['MediaContentType0']
            || ''
        ).trim().toLowerCase();

        return metadataContentType || null;
    }

    private getAttachmentKind(message: WhatsappConversationMessage): string | null {
        const normalizedContentType = this.getAttachmentContentType(message);
        if (normalizedContentType) {
            const [kind, subtype] = normalizedContentType.split('/');
            if (kind === 'application' && subtype === 'pdf') {
                return 'PDF';
            }

            if (kind === 'audio') {
                return 'Audio';
            }

            if (kind === 'video') {
                return 'Video';
            }

            if (kind === 'application') {
                return 'Documento';
            }
        }

        const attachmentUrl = this.getAttachmentUrl(message);
        const extensionMatch = String(attachmentUrl || '').match(/\.([a-z0-9]{2,8})(?:\?.*)?$/i);
        if (!extensionMatch) {
            return null;
        }

        return extensionMatch[1].toUpperCase();
    }

    private normalizeAttachmentSource(value: unknown): string | null {
        const mediaUrl = String(value || '').trim();
        if (!mediaUrl) {
            return null;
        }

        const [firstSegment] = mediaUrl.split(';;');
        const normalizedMediaUrl = String(firstSegment || '').trim();

        return normalizedMediaUrl || null;
    }

    private getRawPayload(message: WhatsappConversationMessage): Record<string, unknown> | undefined {
        return message.metadata?.['raw'] as Record<string, unknown> | undefined;
    }

    private isSystemMessage(message: WhatsappConversationMessage): boolean {
        const origin = String(message.metadata?.['origin'] || '').trim();
        return origin === 'notification_campaign' || origin === 'notification_history_fallback';
    }
}
